import { CustomListItemId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import {
    tierColorForToken,
    tierColorOptions,
} from "components/references/show-reference/tier-colors";
import { trpc } from "components/rpc/trpc";
import { AutosavingInput } from "components/ui/autosaving-input";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import React from "react";

type Tier = inferProcedureOutput<AppRouter["referralTier"]["tiers"]>[number];

export const ReferralTierEditor: React.FC<unknown> = () => {
    const trpcClient = trpc.useTRPC();
    const tiersQuery = useQuery(trpcClient.referralTier.tiers.queryOptions());

    const onSuccess = () => {
        tiersQuery.refetch();
    };

    const createTier = useMutation({
        ...trpcClient.referralTier.createTier.mutationOptions(),
        onSuccess,
    });
    const patchTier = useMutation({
        ...trpcClient.referralTier.patchTier.mutationOptions(),
        onSuccess,
    });

    const tiers: Tier[] = (tiersQuery.data ?? []).filter(t => !t.isArchived);

    const renameTier = (itemId: CustomListItemId, name: string) => {
        patchTier.mutate({ itemId, name, color: null, order: null, isArchived: null });
    };

    const recolorTier = (itemId: CustomListItemId, color: string) => {
        patchTier.mutate({ itemId, name: null, color, order: null, isArchived: null });
    };

    const archiveTier = (itemId: CustomListItemId) => {
        patchTier.mutate({
            itemId,
            name: null,
            color: null,
            order: null,
            isArchived: true,
        });
    };

    return (
        <div className="space-y-4 max-w-2xl">
            {tiers.map(tier => {
                const color = tierColorForToken(tier.color);
                const itemId = tier.id as CustomListItemId;
                return (
                    <div
                        key={tier.id}
                        className="flex flex-wrap items-center gap-3 border-b border-gray-100 pb-3">
                        <div className="flex-1 min-w-[12rem]">
                            <AutosavingInput
                                initial={tier.name}
                                onCommit={newVal => renameTier(itemId, newVal)}
                            />
                        </div>
                        <span
                            className={`inline-block flex-shrink-0 rounded-full ${color.bg} ${color.text} px-3 py-0.5 text-sm font-medium`}>
                            {tier.name || "Tier"}
                        </span>
                        <div className="flex items-center flex-wrap gap-1">
                            {tierColorOptions.map(opt => (
                                <SecondaryCompactButton
                                    key={opt.token}
                                    title={opt.token}
                                    onClick={() => recolorTier(itemId, opt.token)}>
                                    <span
                                        className={`inline-block w-4 h-4 rounded-full ${
                                            opt.bg
                                        } ${
                                            tier.color === opt.token
                                                ? "ring-2 ring-offset-1 ring-gray-600"
                                                : ""
                                        }`}
                                    />
                                </SecondaryCompactButton>
                            ))}
                        </div>
                        <SecondaryCompactButton onClick={() => archiveTier(itemId)}>
                            Remove
                        </SecondaryCompactButton>
                    </div>
                );
            })}
            <PrimaryButton
                onClick={() => createTier.mutate({ name: "New tier", color: "gray" })}>
                Add tier
            </PrimaryButton>
        </div>
    );
};
